import { isClient } from '@/utils/helpers/common';
import axios from 'axios';
import errorMessages from '@/data/err.json';
import { handleApiError } from '@/lib/errorHandler';

const API_BASE_URL = import.meta.env.VITE_BASE_URL;

export const apiConfig = {
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
};

// Get token from localStorage
const getToken = (): string | null => {
  if (isClient) {
    return localStorage.getItem('token');
  }
  return null;
};

// Get current language from localStorage
const getCurrentLanguage = (): string => {
  if (isClient) {
    return localStorage.getItem('lang') || 'en';
  }
  return 'en';
};

export interface ValidationError {
  key: string;
  path: string;
  message: string;
  params?: Record<string, string>;
}

export interface ErrorResponse {
  key?: string;
  message?: string;
  params?: Record<string, string>;
  validations?: ValidationError[];
}

export interface ApiError {
  status?: number;
  data?: {
    error?: ErrorResponse;
    message?: string;
  };
  message: string;
  originalError?: any;
}

/**
 * Hata key'inden çevrilmiş mesajı alır ve params ile değiştirir
 */
const getErrorMessageFromKey = (
  key: string,
  params?: Record<string, string>
): string | null => {
  try {
    const err = (
      errorMessages as unknown as Record<string, Array<Record<string, string>>>
    )[key];

    if (!err || !Array.isArray(err)) {
      return null;
    }

    const lang = getCurrentLanguage();
    const messageObj = err.find(item => item[lang]);

    if (!messageObj || !messageObj[lang]) {
      return null;
    }

    let message = messageObj[lang];

    // Params varsa replace et
    if (params && typeof params === 'object') {
      Object.keys(params).forEach(paramKey => {
        const paramValue = params[paramKey];
        if (paramValue && typeof paramValue === 'string') {
          message = message.replace(
            new RegExp(`\\{${paramKey}\\}`, 'g'),
            paramValue
          );
        }
      });
    }

    return message;
  } catch (error) {
    console.error('Error getting message from key:', error);
    return null;
  }
};

/**
 * API error response'unu formatlar ve çevrilmiş mesaj oluşturur
 */
const formatApiError = (errorData: any): string => {
  try {
    const errorResponse = errorData?.error as ErrorResponse | undefined;

    if (!errorResponse) {
      return errorData?.message || 'Bir hata oluştu';
    }

    // Validations varsa işle
    if (
      errorResponse.key &&
      errorResponse.validations &&
      Array.isArray(errorResponse.validations) &&
      errorResponse.validations.length > 0
    ) {
      // Ana hata mesajını al
      const mainMessage = getErrorMessageFromKey(
        errorResponse.key,
        errorResponse.params
      );

      // Validations içindeki tüm hataları işle
      const validationMessages = errorResponse.validations
        .map((validation: ValidationError) => {
          const { key, params } = validation;
          const validationMessage = getErrorMessageFromKey(key, params);
          return validationMessage;
        })
        .filter(Boolean); // Null olan mesajları filtrele

      // Ana mesaj + validation mesajlarını birleştir
      if (mainMessage) {
        return `${mainMessage}. ${validationMessages.join(' ')}`;
      }
      return validationMessages.join(' ');
    }

    // Sadece key varsa (validations olmadan)
    if (errorResponse.key) {
      const translatedMessage = getErrorMessageFromKey(
        errorResponse.key,
        errorResponse.params
      );

      if (translatedMessage) {
        return translatedMessage;
      }

      // Key bulunamadıysa message göster
      if (errorResponse.message) {
        return errorResponse.message;
      }
    }

    // Fallback
    const fallbackMessage = getErrorMessageFromKey('anErrorOccurred');
    return fallbackMessage || 'Bir hata oluştu';
  } catch (parseError) {
    console.error('Error formatting API error:', parseError);
    const fallbackMessage = getErrorMessageFromKey('anErrorOccurred');
    return fallbackMessage || 'Bir hata oluştu';
  }
};

// API request helper (axios version)
export const apiRequest = async (endpoint: string, options: any = {}) => {
  const url = `${apiConfig.baseURL}${endpoint}`;
  const token = getToken();

  // Merge headers
  const headers = {
    ...apiConfig.headers,
    ...(options.headers || {}),
  };

  // Add authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // axios config
  const config = {
    url,
    method: options.method || 'GET',
    headers,
    params: options.params || {},
    data: options.body ? JSON.parse(options.body) : undefined,
    withCredentials: true,
  };

  try {
    const response = await axios(config);

    // HTTP status code kontrolü
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    } else {
      throw {
        status: response.status,
        data: response.data,
        message: `HTTP ${response.status}: ${response.statusText}`,
      };
    }
  } catch (error: any) {
    if (error.response) {
      const errorData = error.response.data;
      const formattedMessage = formatApiError(errorData);

      // Bazı çağrılar için beklenen/rutin bir hata durumu (ör. "bu kullanıcının
      // affiliate profili var mı" kontrolünde 404) global hata toast'ını
      // tetiklememeli — bkz. options.silent / options.silentStatuses.
      const isSilenced =
        options.silent === true ||
        (Array.isArray(options.silentStatuses) &&
          options.silentStatuses.includes(error.response.status));

      if (!isSilenced) {
        handleApiError({
          status: error.response.status,
          data: errorData,
          message: formattedMessage,
        });
      }

      throw {
        status: error.response.status,
        data: errorData,
        message: formattedMessage,
        originalError: error,
      };
    }

    // Network veya diğer hatalar için genel hata mesajı göster
    console.error('API request failed:', error);
    handleApiError({
      message: error?.message || 'Bir hata oluştu',
    });

    throw error;
  }
};
